import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { formatNumber } from '../../utils/numbersFormatter'
import {
  InfoRounded as Info,
  ShowChartRounded as ShowChart,
  AccountBalanceWalletRounded as AccountBalanceWallet,
  TrendingUpRounded as TrendingUp,
  TrendingDownRounded as TrendingDown,
  PercentRounded as Percent,
  SavingsRounded as Savings,
} from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { tokens } from '../../styles/tokens'
import { useCardHeaderStyles } from '../../styles/cards'
import { ltvTopUpToTarget } from '../../utils/ltv'

const useStyles = makeStyles(theme => ({
  heroBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingTop: theme.spacing(1),
  },
  heroLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: tokens.color.neutral[500],
    margin: 0,
  },
  heroValue: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    color: tokens.color.neutral[900],
    fontVariantNumeric: 'tabular-nums',
    margin: 0,
  },
  statsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    borderTop: `1px solid ${tokens.color.neutral[100]}`,
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: tokens.radius.md,
    flexShrink: 0,
    '& svg': {
      fontSize: '1.05rem',
    },
  },
  statIconNeutral: {
    backgroundColor: tokens.color.primary[50],
    color: tokens.color.primary[600],
  },
  statIconPositive: {
    backgroundColor: tokens.color.success[100],
    color: tokens.color.success[700],
  },
  statIconNegative: {
    backgroundColor: tokens.color.error[100],
    color: tokens.color.error[700],
  },
  statTextWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexGrow: 1,
    minWidth: 0,
  },
  statLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: tokens.color.neutral[500],
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    '& svg': {
      fontSize: '0.9rem',
    },
  },
  statValue: {
    fontSize: '0.875rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    margin: 0,
  },
  statValueNeutral: {
    color: tokens.color.neutral[900],
  },
  statValuePositive: {
    color: tokens.color.success[700],
  },
  statValueNegative: {
    color: tokens.color.error[500],
  },
}))

interface FixedIncomeStatsProps {
  title: string
  HSNumber: number
  ABNumber: number
  TNumber: number
  OYNumber: number
  isLabel: boolean
  showLTV?: boolean
  LTVNumber?: number
  hideExpectedReturn?: boolean
}

type StatTone = 'neutral' | 'positive' | 'negative'

const iconClassByTone = {
  neutral: 'statIconNeutral',
  positive: 'statIconPositive',
  negative: 'statIconNegative',
} as const

const valueClassByTone = {
  neutral: 'statValueNeutral',
  positive: 'statValuePositive',
  negative: 'statValueNegative',
} as const

const StatRow = ({
  classes,
  icon,
  tone,
  label,
  value,
}: {
  classes: ReturnType<typeof useStyles>
  icon: React.ReactNode
  tone: StatTone
  label: React.ReactNode
  value: string
}) => (
  <div className={classes.statRow}>
    <span className={classNames(classes.statIcon, classes[iconClassByTone[tone]])}>{icon}</span>
    <div className={classes.statTextWrapper}>
      <p className={classes.statLabel}>{label}</p>
      <p className={classNames(classes.statValue, classes[valueClassByTone[tone]])}>{value}</p>
    </div>
  </div>
)

const FixedIncomeStats: React.FC<FixedIncomeStatsProps> = ({
  title,
  HSNumber,
  ABNumber,
  TNumber,
  OYNumber,
  isLabel,
  showLTV,
  LTVNumber = 0,
  hideExpectedReturn,
}: FixedIncomeStatsProps) => {
  const classes = useStyles()
  const cardHeader = useCardHeaderStyles()
  const { t } = useTranslation()
  const delta = OYNumber - TNumber

  return (
    <>
      <div className={cardHeader.header}>
        <p className={cardHeader.title}>{t('PORTFOLIO_SUMMARY')}</p>
      </div>
      <div className={classes.heroBlock}>
        <p className={classes.heroLabel}>{t('MARKET_PORTFOLIO_AMOUNT')}</p>
        <p className={classes.heroValue}>{formatNumber(TNumber)} CZK</p>
      </div>
      <div className={classes.statsSection}>
        {!isLabel && (
          <>
            <StatRow classes={classes} icon={<ShowChart />} tone="neutral" label={t('HOLDIN_SECURITIES')} value={`${formatNumber(HSNumber)} CZK`} />
            <StatRow
              classes={classes}
              icon={<AccountBalanceWallet />}
              tone={ABNumber >= 0 ? 'neutral' : 'negative'}
              label={t('ACCOUNT_BALANCE_IN_CZK')}
              value={`${formatNumber(ABNumber)} CZK`}
            />
            {showLTV && (
              <>
                <StatRow classes={classes} icon={<Percent />} tone="neutral" label={t('LTV')} value={`${formatNumber(LTVNumber * 100)} %`} />
                <StatRow classes={classes} icon={<Savings />} tone="neutral" label={t('LTV_TOPUP_50')} value={`${formatNumber(ltvTopUpToTarget(-ABNumber, HSNumber, 0.5))} CZK`} />
                <StatRow classes={classes} icon={<Savings />} tone="neutral" label={t('LTV_TOPUP_70')} value={`${formatNumber(ltvTopUpToTarget(-ABNumber, HSNumber, 0.7))} CZK`} />
              </>
            )}
          </>
        )}
        {!hideExpectedReturn && (
          <StatRow
            classes={classes}
            icon={delta >= 0 ? <TrendingUp /> : <TrendingDown />}
            tone={delta >= 0 ? 'positive' : 'negative'}
            label={
              <>
                {t('EXPECTED_PORTFOLIO_AMOUNT')}
                <Tooltip title={t('SUMMARY_DISCLAIMER')}><Info /></Tooltip>
              </>
            }
            value={`${delta >= 0 ? '+' : ''}${formatNumber(delta)} CZK`}
          />
        )}
      </div>
    </>
  )
}

export default FixedIncomeStats
